import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './SectionStatsAndProcess.module.css';
import config from '../../../config/config';
import { t } from '../../../util/messages';

/**
 * How we work — the dark anchor of the page.
 *
 * Two blocks: the four-stage delivery process, then the three commercial
 * models. The headline statistics that used to sit here now live in the hero
 * stats deck, so they are not repeated.
 */
const SectionStatsAndProcess = ({ rootClassName, className }) => {
  const classes = classNames(rootClassName || css.root, className);
  const processConfig = config.configProcess;
  const engagementConfig = config.configEngagement;
  const aiPrinciples = config.configAiPrinciples;

  return (
    <section id="stats-and-process" className={classes} aria-label="How we work">
      <div className={css.content}>
        <header className={css.header} data-reveal>
          <div>
            <span className={css.tag}>
              <i aria-hidden="true" />
              {t('SectionStatsAndProcess.eyebrow')}
            </span>
            <h2 className={css.title}>{t('SectionStatsAndProcess.heading')}</h2>
          </div>
          <p className={css.description}>{t('SectionStatsAndProcess.title')}</p>
        </header>

        {/* An ordered list: the stages happen in sequence, and that matters. */}
        <ol className={css.process}>
          {processConfig.map((stage, index) => (
            <li key={stage.id} className={css.stage} data-reveal data-reveal-delay={index * 80}>
              <div className={css.icon}>
                <img src={stage.icon} alt="" aria-hidden="true" width="30" height="30" />
              </div>
              <span className={css.stepNumber}>
                {t('SectionStatsAndProcess.step', { number: String(index + 1).padStart(2, '0') })}
              </span>
              <h3 className={css.label}>{stage.title}</h3>
              <p className={css.stageDescription}>{stage.description}</p>
            </li>
          ))}
        </ol>

        <div className={css.models}>
          {engagementConfig.map((model, index) => (
            <article
              key={model.id}
              /* Anchor target: the Cooperation Models menu links here. */
              id={model.id}
              className={css.model}
              data-reveal
              data-reveal-delay={index * 80}
            >
              <h3 className={css.modelTitle}>{model.title}</h3>
              <p className={css.modelDescription}>{model.description}</p>
              <p className={css.modelBestFor}>
                <span>{t('SectionStatsAndProcess.bestFor')}</span>
                {model.bestFor}
              </p>
            </article>
          ))}
        </div>

        {/*
          Data-handling principles. Deliberately placed with the commercial
          models rather than buried in a policy page: for an AI engagement
          these are part of the offer, not the small print.
        */}
        <div className={css.principles}>
          {aiPrinciples.map((principle, index) => (
            <div
              key={principle.id}
              className={css.principle}
              data-reveal
              data-reveal-delay={index * 80}
            >
              <span className={css.principleLabel}>{principle.label}</span>
              <p className={css.principleTitle}>{principle.title}</p>
              <p className={css.principleDescription}>{principle.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

SectionStatsAndProcess.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(SectionStatsAndProcess);
